import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Handle both ISO (yyyy-MM-dd) and dd/MM/yyyy formats

const PRIMARY = '#263078';
const PRIMARY_MID = '#3949ab';
const PRIMARY_LT = '#e8eaf6';

const th  = { padding: '6px 8px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td  = { padding: '5px 8px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '5px 8px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '5px 8px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const statusStyle = (status, daysDiff) => {
  const d = parseInt(daysDiff) || 0;
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'paid') return { bg: '#d1fae5', color: '#059669' };
  if (s.includes('unpaid'))              return { bg: '#fecaca', color: '#dc2626' };
  if (s.includes('partial') && d > 0)   return { bg: '#fef3c7', color: '#b45309' };
  if (s.includes('partial'))             return { bg: '#dbeafe', color: '#1d4ed8' };
  if (d > 0)                             return { bg: '#fecaca', color: '#dc2626' };
  return                                        { bg: '#f3f4f6', color: '#6b7280' };
};

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DelayedCollectionReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [fetched, setFetched]       = useState(false);
  const [search, setSearch]         = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [expanded, setExpanded]     = useState({});
  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'FinancialStatementsReportApi',
        `/DelayedCollectionReport/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
      setExpanded({});
    } catch (err) {
      console.error(err);
      setData([]);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  // Group: BillNo ഉള്ള row = master, CollectedDate/PaidAmount ഉള്ള rows = transactions
  const masters = useMemo(() => {
    return data.reduce((acc, row) => {
      if (row.BillNo) {
        acc.push({
          SaleId: row.SaleId,
          CustomerName: row.CustomerName,
          BillNo: row.BillNo,
          BillDate: row.BillDate,
          DueDate: row.DueDate,
          TotalAmount: row.TotalAmount,
          TotalPaidAmount: row.TotalPaidAmount,
          BalanceAmount: row.BalanceAmount,
          DaysDiff: row.DaysDiff,
          Status: row.Status,
          transactions: [],
        });
      } else if ((row.PaidAmount || row.CollectedDate) && acc.length > 0) {
        acc[acc.length - 1].transactions.push({
          PaidAmount: row.PaidAmount,
          CollectedDate: row.CollectedDate,
        });
      }
      return acc;
    }, []);
  }, [data]);

  const filtered = useMemo(() => {
    if (!search.trim()) return masters;
    const q = search.toLowerCase();
    return masters.filter(m =>
      (m.CustomerName || '').toLowerCase().includes(q) ||
      (m.BillNo       || '').toLowerCase().includes(q) ||
      (m.Status       || '').toLowerCase().includes(q)
    );
  }, [masters, search]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const totalAmount  = filtered.reduce((s, r) => s + (parseFloat(r.TotalAmount)      || 0), 0);
  const totalPaid    = filtered.reduce((s, r) => s + (parseFloat(r.TotalPaidAmount)   || 0), 0);
  const totalBalance = filtered.reduce((s, r) => s + (parseFloat(r.BalanceAmount)     || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'DelayedCollectionReport', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Customer', 'Bill No', 'Bill Date', 'Due Date', 'Total Amt', 'Paid Amt', 'Balance', 'Days', 'Status'];
    const rows = [];
    filtered.forEach((m, i) => {
      rows.push([i + 1, m.CustomerName, m.BillNo, fmtDate(m.BillDate), fmtDate(m.DueDate),
        parseFloat(m.TotalAmount) || 0, parseFloat(m.TotalPaidAmount) || 0,
        parseFloat(m.BalanceAmount) || 0, parseInt(m.DaysDiff) || 0, m.Status || '']);
      m.transactions.forEach(t => {
        rows.push(['', '', '', '', '', '', '', '', '', '', '', fmtDate(t.CollectedDate), parseFloat(t.PaidAmount) || 0]);
      });
    });

      await exportStyledExcel({
          reportTitle: 'DELAYED COLLECTION REPORT',
          sheetName: 'Delayed Collection',
          fileName: `DelayedCollectionReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: cols,
          rows,
          totalsRow: ['', '', '', '', 'Totals', totalAmount, totalPaid, totalBalance, '', ''],
      });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 8, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading delayed collection report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '4px 10px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a237e 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Delayed Collection Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search customer, bill..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 110px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 950 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={{ ...th, width: 32 }}></th>
                <th style={th}>#</th>
                <th style={th}>Customer</th>
                <th style={th}>Bill No</th>
                <th style={th}>Bill Date</th>
                <th style={th}>Due Date</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Amt</th>
                <th style={{ ...th, textAlign: 'right' }}>Paid Amt</th>
                <th style={{ ...th, textAlign: 'right' }}>Balance</th>
                <th style={{ ...th, textAlign: 'center' }}>Days</th>
                <th style={{ ...th, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    No records found
                  </td>
                </tr>
              ) : (
                filtered.map((m, i) => {
                  const isOpen = !!expanded[m.SaleId];
                  const hasTx  = m.transactions.length > 0;
                  const ss     = statusStyle(m.Status, m.DaysDiff);
                  const days   = parseInt(m.DaysDiff) || 0;
                  return (
                    <>
                      {/* Master Row */}
                      <tr key={m.SaleId || i}
                        onClick={() => hasTx && toggleExpand(m.SaleId)}
                        style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9', cursor: hasTx ? 'pointer' : 'default' }}>
                        <td style={{ ...tdC, width: 32 }}>
                          {hasTx
                            ? isOpen
                              ? <KeyboardArrowDown sx={{ fontSize: 16, color: PRIMARY }} />
                              : <KeyboardArrowRight sx={{ fontSize: 16, color: '#9ca3af' }} />
                            : null}
                        </td>
                        <td style={td}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 600, color: '#1e293b', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.CustomerName}</td>
                        <td style={{ ...td, color: PRIMARY, fontWeight: 600 }}>{m.BillNo}</td>
                        <td style={td}>{fmtDate(m.BillDate)}</td>
                        <td style={{ ...td, color: days > 0 ? '#dc2626' : '#059669', fontWeight: 600 }}>{fmtDate(m.DueDate)}</td>
                        <td style={tdR}>{fmt(m.TotalAmount)}</td>
                        <td style={{ ...tdR, color: '#059669', fontWeight: 600 }}>{fmt(m.TotalPaidAmount)}</td>
                        <td style={{ ...tdR, color: (parseFloat(m.BalanceAmount) || 0) > 0 ? '#dc2626' : '#374151', fontWeight: 600 }}>{fmt(m.BalanceAmount)}</td>
                        <td style={tdC}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: days > 0 ? '#dc2626' : days < 0 ? '#059669' : '#d97706' }}>
                            {days > 0 ? `+${days}` : days}
                          </span>
                        </td>
                        <td style={tdC}>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: ss.bg, color: ss.color }}>
                            {m.Status || '-'}
                          </span>
                        </td>
                      </tr>

                      {/* Transaction Sub-Header */}
                      {isOpen && (
                        <tr style={{ background: '#c7d2fe', borderBottom: '1px solid #a5b4fc' }}>
                          <td style={tdC}></td>
                          <td style={{ ...td, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>#</td>
                          <td colSpan={3} style={{ ...td, fontWeight: 700, fontSize: 11, color: '#1e1b4b', paddingLeft: 24 }}>Collected Date</td>
                          <td colSpan={6} style={{ ...tdR, fontWeight: 700, fontSize: 11, color: '#1e1b4b' }}>Paid Amount</td>
                        </tr>
                      )}

                      {/* Transaction Rows */}
                      {isOpen && m.transactions.map((t, j) => (
                        <tr key={`${m.SaleId}-${j}`} style={{ background: '#eef2ff', borderBottom: '1px solid #e0e7ff' }}>
                          <td style={tdC}></td>
                          <td style={{ ...td, color: '#6b7280' }}>{j + 1}</td>
                          <td colSpan={3} style={{ ...td, color: '#374151', paddingLeft: 24 }}>{fmtDate(t.CollectedDate)}</td>
                          <td colSpan={6} style={{ ...tdR, fontWeight: 600, color: '#059669' }}>{fmt(t.PaidAmount)}</td>
                        </tr>
                      ))}
                    </>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={6} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmt(totalAmount)}</td>
                  <td style={tdR}>{fmt(totalPaid)}</td>
                  <td style={tdR}>{fmt(totalBalance)}</td>
                  <td colSpan={2} style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default DelayedCollectionReport;




